import { DynamicQuestionComponent } from '#app/features/form/fields/dynamicQuestionComponent.tsx'
import { generateDynamicStepsFromData } from '#app/features/form/fields/generateDynamicStepsFromData.tsx'
import { RadioButtonComponent } from '#app/features/form/fields/radioFieldComponent.tsx'
import { TextFieldComponent } from '#app/features/form/fields/textFieldComponent.tsx'
import { getCurrentStepSchema } from '#app/features/form/wizard/utils/getCurrentStepSchema.ts'
import { getAllQuestionsFromTree } from '#app/features/form/wizard/utils/getFromJSONTree.ts'
import {
	type GetStepsParams,
	type RegisterFormHook,
	type Steps,
} from '#types/form/wizard.ts'
import { type CombinedIpipType } from '../../personalityTests/ipipNeo/validationSchemas.ts'
import {
	personalChallengesTree,
	interpersonalChallengesTree,
} from './dynamicTrees.ts'
import { primaryNeedZod, questionZod } from './validationSchemas.ts'

export function GetStepsSession({
	currentQuestionId,
	questionTree,
	currentQuestionTreeId,
	answeredQuestionsStack,
	editableParents,
	needsController = false,
	t,
}: GetStepsParams): Steps<any>[] {
	const generateDynamic = generateDynamicStepsFromData({
		exemptions: ['personalChallenges', 'interpersonalChallenges'],
	})({
		answeredQuestionsStack,
		currentQuestionTreeId,
		questionTree,
		editableParents,
	})

	return [
		{
			id: 'primaryNeed',
			fieldKey: 'primaryNeed',
			isController: true,
			isInJsonTree: false,
			hasBackendCall: false,
			needsToBeIncludedByController: false,
			validator: primaryNeedZod,
			component: ({ control, getValues, dispatch }: RegisterFormHook<any>) => (
				<>
					<RadioButtonComponent
						isController
						control={control}
						dispatch={dispatch}
						getValues={getValues}
						fieldName="primaryNeed"
						id="primaryNeed"
						label={t('form.questionLabel')}
						values={[
							{
								id: 'personal-challenges',
								includedQuestions: ['personalChallenges'],
								title: t('form.options.personalChallenges.title'),
								subtitle: t('form.options.personalChallenges.subtitle'),
							},
							{
								id: 'interpersonal-challenges',
								includedQuestions: ['interpersonalChallenges'],
								title: t('form.options.interpersonalChallenges.title'),
								subtitle: t('form.options.interpersonalChallenges.subtitle'),
							},
							{
								id: 'explore-feelings',
								includedQuestions: ['exploreFeelings'],
								title: t('form.options.exploreFeelings.title'),
								subtitle: t('form.options.exploreFeelings.subtitle'),
							},
						]}
					/>
				</>
			),
		},
		{
			id: 'personalChallenges',
			hasBackendCall: true,
			backendAction: 'save',
			isController: true,
			needsToBeIncludedByController: needsController,
			isInJsonTree: true,
			fieldKey:
				`personalChallenges-${currentQuestionId}` as keyof CombinedIpipType,
			validator: getCurrentStepSchema(
				`personalChallenges-${currentQuestionId}`,
			),
			questionText: getAllQuestionsFromTree(
				personalChallengesTree(t),
				'personalChallenges',
			),
			component: ({
				register,
				getValues,
				dispatch,
				control,
				watch,
				handleSubmit,
			}: RegisterFormHook<any>) => (
				<>
					<DynamicQuestionComponent
						isController
						control={control}
						watch={watch}
						getValues={getValues}
						dispatch={dispatch}
						register={register}
						handleSubmit={handleSubmit}
						key={currentQuestionId}
						fieldKey={`personalChallenges-${currentQuestionId}`}
						questionTree={questionTree}
						answeredQuestionsStack={answeredQuestionsStack}
						currentQuestionTreeId={'personalChallenges'}
					/>
				</>
			),
		},
		{
			id: 'interpersonalChallenges',
			hasBackendCall: true,
			backendAction: 'save',
			isController: true,
			needsToBeIncludedByController: needsController,
			isInJsonTree: true,
			fieldKey:
				`interpersonalChallenges-${currentQuestionId}` as keyof CombinedIpipType,
			validator: getCurrentStepSchema(
				`interpersonalChallenges-${currentQuestionId}`,
			),
			questionText: getAllQuestionsFromTree(
				interpersonalChallengesTree(t),
				'interpersonalChallenges',
			),
			component: ({
				register,
				getValues,
				dispatch,
				control,
				watch,
				handleSubmit,
			}: RegisterFormHook<any>) => (
				<>
					<DynamicQuestionComponent
						isController
						control={control}
						watch={watch}
						getValues={getValues}
						dispatch={dispatch}
						register={register}
						handleSubmit={handleSubmit}
						key={currentQuestionId}
						fieldKey={`interpersonalChallenges-${currentQuestionId}`}
						questionTree={questionTree}
						answeredQuestionsStack={answeredQuestionsStack}
						currentQuestionTreeId={'interpersonalChallenges'}
					/>
				</>
			),
		},
		{
			id: 'question',
			fieldKey: 'question',
			isInJsonTree: false,
			needsToBeIncludedByController: false,
			isController: false,
			hasBackendCall: true,
			backendAction: 'judgeRateCreate',
			questionText: [
				{
					id: 'question',
					text: t('form.question', { ns: 'intakeTrees' }),
				},
			],
			validator: questionZod,
			component: ({
				register,
				getValues,
				dispatch,
				watch,
				t,
			}: RegisterFormHook<any>) => {
				return (
					<div className="h-full w-full">
						<TextFieldComponent
							canBeParent
							isParentProps={{
								id: 'question',
								questionTree: questionTree as any,
							}}
							editableParents={editableParents}
							watch={watch}
							getValues={getValues}
							dispatch={dispatch}
							register={register}
							type="textArea"
							fieldName="question"
							label={t!('form.question', { ns: 'intakeTrees' })}
						/>
					</div>
				)
			},
		},
		...generateDynamic({
			id: 'question',
			backendAction: 'judgeRateCreate',
			needsToBeIncludedByController: false,
		}),
	]
}
